import { Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { ConfirmationDialogDirective } from '@xmedical/ui';
import { MedicationDisplayItem, MedicationRemovedEvent } from './medication-widget-view.component';
import { hasReferralInfo, formatReferralSource } from '../../types/referral-info';

@Component({
  selector: 'xm-medication-list-view',
  imports: [
    DatePipe,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    ConfirmationDialogDirective
  ],
  template: `
    <div class="mat-card-list">
      @for (item of items(); track item.id; let i = $index) {
        <mat-card class="compact"
                  [class.referred]="isReferred(item)"
                  [class.duplicates]="hasDuplicates(item)"
                  [class.duplicate-types]="hasDuplicateTypes(item)">
          <mat-card-content>
            <mat-card-title class="d-flex align-items-start mat-bold mb-2"
                            [class.primary]="usePrimaryText()"
                            [style.color]="item.textColour || null">
              {{ item.description }} {{ item.dosage }} {{ item.diagnosisCode }}
              <span class="flex-spacer"></span>
              @if (!readonly()) {
                <button
                  class="mat-card-button-top-right-corner"
                  mat-icon-button
                  xuiConfirmationDialog
                  [confirmMessage]="'Are you sure you want to remove ' + item.description + '?'"
                  (confirm)="onRemoveItem(item, i)">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </mat-card-title>
            @if (isReferred(item)) {
              <div class="referral-indicator">
                <mat-icon>send</mat-icon>
                <span>{{ getReferralText(item) }}</span>
              </div>
            }
            @if (item.comment) {
              <span class="item-comment">{{ item.comment }}</span>
            }
            <mat-card-subtitle class="d-flex justify-content-between">
              <span>{{ item.lastModifiedDate | date:'yyyy-MM-dd HH:mm' }}</span>
              @if (item.lastModifiedUser) {
                <span>{{ item.lastModifiedUser.firstName }} {{ item.lastModifiedUser.surname }}</span>
              }
            </mat-card-subtitle>
          </mat-card-content>
          <mat-divider></mat-divider>
        </mat-card>
      }

      @if (!hasItems()) {
        <mat-card>
          <mat-card-content>
            <span>{{ emptyMessage() }}</span>
          </mat-card-content>
        </mat-card>
      }
    </div>
  `,
  styleUrls: ['./medication-list-view.component.scss']
})
export class MedicationListViewComponent {
  items = input<MedicationDisplayItem[]>([]);
  readonly = input<boolean>(true);
  usePrimaryText = input<boolean>(false);
  emptyMessage = input<string>('No medication items have been added.');

  itemRemoved = output<MedicationRemovedEvent>();

  hasItems = computed(() => this.items().length > 0);

  onRemoveItem(item: MedicationDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  isReferred(item: MedicationDisplayItem): boolean {
    return hasReferralInfo(item);
  }

  getReferralText(item: MedicationDisplayItem): string {
    return formatReferralSource(item);
  }

  hasDuplicates(item: MedicationDisplayItem): boolean {
    return this.items().some(m => {
      if (m.description.length > item.description.length) {
        return m.description.indexOf(item.description) > -1 && m.id !== item.id;
      } else if (m.description.length < item.description.length) {
        return item.description.indexOf(m.description) > -1 && m.id !== item.id;
      } else {
        return m.description === item.description && m.id !== item.id;
      }
    });
  }

  hasDuplicateTypes(item: MedicationDisplayItem): boolean {
    if (item.typeId === undefined || item.typeUnassigned) {
      return false;
    }
    return this.items().some(m => {
      return m.typeId !== undefined && item.typeId === m.typeId && item.id !== m.id;
    });
  }
}
